import { useEffect, useRef, useState } from 'react';
import type {
  BrigadeStation,
  CoverageBorderFeatureCollection,
  CoverageBounds,
  CoverageFeatureCollection,
  LngLat,
  ResponseOrderResult,
  StationAvailability,
  TurnoutProfile,
} from '@duty/shared';
import { defaultRoadGraphUrl } from '@/incident/coverage';
import type { CoverageComputeRequest, CoverageWorkerMessage } from '@/workers/coverage.protocol';

export interface CoverageOverrides {
  /** Partial turn-out overrides keyed by station id — merged over the seed defaults (scenario mode). */
  turnout: Record<string, Partial<TurnoutProfile>>;
  availability: Record<string, StationAvailability>;
}

export function emptyCoverageOverrides(): CoverageOverrides {
  return { turnout: {}, availability: {} };
}

export interface CoverageQuery {
  /** Candidate stations for this query — the caller prunes (see candidateStationsNear). */
  stations: BrigadeStation[];
  /** Base (pre-override) turnout profiles, restricted to `stations`. */
  turnoutProfiles: Record<string, TurnoutProfile>;
  /** Present for a specific-incident lookup (response order); absent for area coverage-planning mode. */
  incidentPoint?: LngLat;
}

export type CoverageShareEntry = NonNullable<Extract<CoverageWorkerMessage, { type: 'result' }>['coverageShare']>[number];

interface UseCoverageResult {
  loading: boolean;
  error: string | undefined;
  responseOrder: ResponseOrderResult | undefined;
  grid: CoverageFeatureCollection | undefined;
  gridBorders: CoverageBorderFeatureCollection | undefined;
  coverageShare: CoverageShareEntry[] | undefined;
  graphSource: string | undefined;
}

let requestCounter = 0;

/**
 * Runs the coverage worker for a pruned station candidate set. `overrides`
 * drives scenario mode (turn-out sliders / availability toggles) without
 * touching any persisted schema — recomputation happens entirely in the
 * worker from retained fields, so it's cheap even though every change
 * re-posts a full request. Dependencies are value-keyed (JSON strings), not
 * reference-keyed, so callers don't need to hand-memoize every object.
 */
export function useCoverage(
  query: CoverageQuery,
  overrides: CoverageOverrides,
  gridBounds?: CoverageBounds,
  gridCellSizeDeg = 0.01,
): UseCoverageResult {
  const workerRef = useRef<Worker | null>(null);
  const [result, setResult] = useState<CoverageWorkerMessage | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const latestRequestId = useRef<string | null>(null);

  useEffect(() => {
    const worker = new Worker(new URL('../workers/coverage.worker.ts', import.meta.url), { type: 'module' });
    workerRef.current = worker;
    worker.onmessage = (ev: MessageEvent<CoverageWorkerMessage>) => {
      if (ev.data.requestId !== latestRequestId.current) return; // stale response
      setResult(ev.data);
      setLoading(false);
    };
    return () => {
      worker.terminate();
      workerRef.current = null;
    };
  }, []);

  const stationsKey = query.stations.map((s) => s.id).join(',');
  const turnoutKey = JSON.stringify(query.turnoutProfiles);
  const overrideTurnoutKey = JSON.stringify(overrides.turnout);
  const availabilityKey = JSON.stringify(overrides.availability);
  const gridKey = gridBounds ? JSON.stringify(gridBounds) : undefined;

  useEffect(() => {
    const worker = workerRef.current;
    if (!worker || query.stations.length === 0) return;
    const mergedTurnout: Record<string, TurnoutProfile> = {};
    for (const [id, base] of Object.entries(query.turnoutProfiles)) {
      mergedTurnout[id] = { ...base, ...overrides.turnout[id] };
    }

    const requestId = `req_${++requestCounter}`;
    latestRequestId.current = requestId;
    setLoading(true);
    const message: CoverageComputeRequest = {
      type: 'compute',
      requestId,
      incident: query.incidentPoint,
      stations: query.stations,
      graphUrl: defaultRoadGraphUrl(),
      turnoutProfiles: mergedTurnout,
      availability: overrides.availability,
      grid: gridBounds ? { bounds: gridBounds, cellSizeDeg: gridCellSizeDeg } : undefined,
    };
    worker.postMessage(message);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    stationsKey,
    turnoutKey,
    overrideTurnoutKey,
    availabilityKey,
    query.incidentPoint?.lng,
    query.incidentPoint?.lat,
    gridKey,
    gridCellSizeDeg,
  ]);

  const resultData = result?.type === 'result' ? result : undefined;
  const errorMessage = result?.type === 'error' ? result.message : undefined;

  return {
    loading,
    error: errorMessage,
    responseOrder: resultData?.responseOrder,
    grid: resultData?.grid,
    gridBorders: resultData?.gridBorders,
    coverageShare: resultData?.coverageShare,
    graphSource: resultData?.graphMeta.source,
  };
}
